import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Link, useParams, useHistory } from 'react-router-dom'
import { Button, Form, FormGroup, Input, Label } from 'reactstrap'

import { loginSuccess } from '../../store/actions'
import {
    applyAuthSession,
    aceptarInvitacionEmpresa,
    getServerMessage,
    verificarInvitacion
} from '../../helpers/empresaUsuarios'

const AceptarInvitacion = ({ authProfile, loginSuccess }) => {
    const { token } = useParams()
    const history = useHistory()
    const [pageState, setPageState] = useState('loading')
    const [invitationData, setInvitationData] = useState(null)
    const [form, setForm] = useState({ nombre: '', password: '', confirmPassword: '' })
    const [formError, setFormError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        let mounted = true

        const verify = async () => {
            try {
                const response = await verificarInvitacion(token)

                if (!mounted) return

                if (!response.data?.success) {
                    const code = response.data?.code
                    setPageState(code === 'INVITATION_INVALID' ? 'invalid' : 'expired')
                    return
                }

                const data = response.data.data
                setInvitationData(data)
                setForm(prev => ({ ...prev, nombre: data.nombre || '' }))

                if (data.state === 'new_user') {
                    setPageState('new_user')
                } else {
                    if (authProfile) {
                        setPageState('existing_logged')
                    } else {
                        sessionStorage.setItem('pendingInvitationToken', token)
                        setPageState('existing_guest')
                    }
                }
            } catch (error) {
                if (!mounted) return

                const code = error?.response?.data?.code
                setPageState(code === 'INVITATION_INVALID' ? 'invalid' : 'expired')
            }
        }

        verify()

        return () => { mounted = false }
    }, [token, authProfile])

    const _handleAccept = async () => {
        setFormError('')
        setSubmitting(true)

        try {
            const response = await aceptarInvitacionEmpresa({ token })

            if (!response.data?.success) {
                setFormError(response.data?.message || 'No pude aceptar la invitación.')
                setSubmitting(false)
                return
            }

            applyAuthSession(response.data, loginSuccess)
            history.push('/dashboard')
        } catch (error) {
            setFormError(getServerMessage(error, 'No pude aceptar la invitación.'))
            setSubmitting(false)
        }
    }

    const _handleNewUserSubmit = async (event) => {
        event.preventDefault()
        setFormError('')

        const { nombre, password, confirmPassword } = form

        if (!nombre.trim()) {
            setFormError('El nombre es requerido.')
            return
        }

        if (!password || password.length < 8) {
            setFormError('La contraseña debe tener al menos 8 caracteres.')
            return
        }

        if (password !== confirmPassword) {
            setFormError('Las contraseñas no coinciden.')
            return
        }

        setSubmitting(true)

        try {
            const response = await aceptarInvitacionEmpresa({ token, password })

            if (!response.data?.success) {
                setFormError(response.data?.message || 'No pude crear la cuenta.')
                setSubmitting(false)
                return
            }

            applyAuthSession(response.data, loginSuccess)
            history.push('/dashboard')
        } catch (error) {
            setFormError(getServerMessage(error, 'No pude crear la cuenta.'))
            setSubmitting(false)
        }
    }

    const _handleFormChange = (field) => (e) => {
        const value = e.target.value
        setForm(prev => ({ ...prev, [field]: value }))
    }

    const renderFooter = () => (
        <div className="login-footer">
            <p>Copyright © {new Date().getFullYear()}. Todos los Derechos Reservados.</p>
            <p>Hecho con amor por <a target="_blank" rel="noopener noreferrer" href="https://juvasoft.com/?utm_source=timbrat&utm_medium=invitacion">Juvasoft</a></p>
        </div>
    )

    return (
        <div className="login">
            <div className="wrapper">
                <div className="logo">
                    <img alt="Timbrát" src="https://timbrat.com.mx/images/timbrat.svg" />
                </div>

                <div className="form">
                    {pageState === 'loading' && (
                        <p className="tagline">Verificando invitación...</p>
                    )}

                    {(pageState === 'expired' || pageState === 'invalid') && (
                        <>
                            <h1>Oops.</h1>
                            <p className="tagline">Esta invitación expiró o ya fue utilizada.</p>
                            <p className="tagline">Pide al administrador de la empresa que te envíe una nueva invitación.</p>
                        </>
                    )}

                    {pageState === 'existing_guest' && (
                        <>
                            <h1>¡Hola!</h1>
                            <p className="tagline">
                                Te invitaron a administrar <strong>{invitationData?.empresaNombre}</strong> en Timbrát.
                            </p>
                            <p className="tagline">Ya tienes una cuenta. Inicia sesión para aceptar la invitación.</p>
                            <div className="form-actions">
                                <Link to="/login" className="btn btn-primary btn-rounded waves-effect waves-light mb-2">
                                    Iniciar sesión
                                </Link>
                            </div>
                        </>
                    )}

                    {pageState === 'existing_logged' && (
                        <>
                            <h1>¡Hola!</h1>
                            <p className="tagline">
                                Te invitaron a administrar <strong>{invitationData?.empresaNombre}</strong> en Timbrát.
                            </p>
                            {formError && <p style={{ color: '#dc3545' }}>{formError}</p>}
                            <div className="form-actions">
                                <Button
                                    color="primary"
                                    className="btn-rounded waves-effect waves-light mb-2"
                                    onClick={_handleAccept}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Aceptando...' : 'Aceptar invitación'}
                                </Button>
                            </div>
                        </>
                    )}

                    {pageState === 'new_user' && (
                        <>
                            <h1>Bienvenido.</h1>
                            <p className="tagline">
                                Te invitaron a administrar <strong>{invitationData?.empresaNombre}</strong>.<br />
                                Crea tu contraseña para activar tu cuenta.
                            </p>
                            <Form onSubmit={_handleNewUserSubmit}>
                                <FormGroup>
                                    <Label htmlFor="nombre">Nombre</Label>
                                    <Input
                                        id="nombre"
                                        type="text"
                                        value={form.nombre}
                                        onChange={_handleFormChange('nombre')}
                                        required
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label htmlFor="password">Contraseña</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={form.password}
                                        onChange={_handleFormChange('password')}
                                        minLength={8}
                                        required
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        value={form.confirmPassword}
                                        onChange={_handleFormChange('confirmPassword')}
                                        required
                                    />
                                </FormGroup>
                                {formError && <p style={{ color: '#dc3545' }}>{formError}</p>}
                                <div className="form-actions">
                                    <Button
                                        type="submit"
                                        color="primary"
                                        className="btn-rounded waves-effect waves-light mb-2"
                                        disabled={submitting}
                                    >
                                        {submitting ? 'Creando cuenta...' : 'Crear cuenta y aceptar invitación'}
                                    </Button>
                                </div>
                            </Form>
                        </>
                    )}

                    {renderFooter()}
                </div>
            </div>
        </div>
    )
}

const mapStateToProps = (state) => ({
    authProfile: state.Auth.profile || null
})

export default connect(mapStateToProps, { loginSuccess })(AceptarInvitacion)
